import { Flex, Text } from "@chakra-ui/react";
import { useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useIdentity from "@/features/ui/hooks/use-identity";
import useUpdateOrderSheetSetToBeSubmitted from "@/features/order-status/hooks/use-update-order-sheet-set-to-be-submitted";
import { Boutique, Company } from "@/features/types";

interface ToBeSubmittedInlineEditProps {
  orderSheetSetId: number;
  value: Boutique | null;
  // 저장 성공 시 폼 값 동기화용
  onSaved: (value: Boutique | null) => void;
  // 저장 성공 시 서버 데이터 재검증(mutate) 트리거용
  onUpdated?: () => void;
}

/**
 * 제출처(오더시트세트 단위) 인라인 편집 — 수수료 인라인 수정과 같은 관습.
 * "수정" 클릭 → 부티크 드랍다운 + 저장/취소. 세트 안 모든 오더시트(취소 제외)와
 * 세트의 제출처가 함께 바뀐다(서버가 원자적으로 처리, 부분 적용 없음).
 */
export default function ToBeSubmittedInlineEdit({
  orderSheetSetId,
  value,
  onSaved,
  onUpdated,
}: ToBeSubmittedInlineEditProps) {
  const identity = useIdentity();
  const toasts = useAppToasts();
  const { fire, isLoading } = useUpdateOrderSheetSetToBeSubmitted();
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<Company | null>(value);

  const canEdit =
    !!identity?.company && identity.company.type === "AGENCY" && !isLoading;

  const startEdit = () => {
    setSelected(value);
    setIsEditing(true);
  };

  const save = () => {
    if (!canEdit || !identity?.company) {
      return;
    }
    fire(
      { agencyId: identity.company.id, orderSheetSetId },
      { toBeSubmitted: selected ? { id: selected.id } : null },
      { rethrow: true }
    )
      .then((response) => {
        toasts.info("제출처가 수정되었습니다.");
        // 응답에 제출처가 비어 오면(구버전 서버가 응답 모델에서 걷어낸 경우 등)
        // 방금 고른 값으로 보정한다 — 저장은 성공했으니 화면도 그 값이어야 한다.
        onSaved(response?.toBeSubmitted ?? (selected as Boutique | null));
        setIsEditing(false);
        // 시트별 제출처·파일명도 함께 바뀌므로 서버 데이터를 재검증한다.
        onUpdated?.();
      })
      .catch((e) => {
        toasts.error(e);
      });
  };

  return (
    <Flex alignItems={"center"} gap={"6px"} flexWrap={"wrap"}>
      {!isEditing ? (
        <>
          <Text fontSize={"12px"}>{value?.name ?? "-"}</Text>
          {canEdit && (
            <Text
              fontSize={"12px"}
              color={"#1272EF"}
              cursor={"pointer"}
              onClick={startEdit}
            >
              수정
            </Text>
          )}
        </>
      ) : (
        <Flex flexDirection={"column"} gap={"6px"} width={"100%"}>
          <Flex width={"180px"}>
            <AppCompanySelect
              width={"100%"}
              name={"제출처"}
              types={["BOUTIQUE"]}
              id={selected ? selected.id : -1}
              isClearable={false}
              isNullable={true}
              onSelect={(company) => {
                if (company) {
                  setSelected(company);
                }
              }}
            />
          </Flex>
          {/* 저장/취소는 드랍다운 다음 줄에 */}
          <Flex gap={"6px"}>
            <AppButton
              size={"small"}
              variant={"primary"}
              isLoading={isLoading}
              onClick={save}
            >
              저장
            </AppButton>
            <AppButton
              size={"small"}
              variant={"gray"}
              onClick={() => setIsEditing(false)}
            >
              취소
            </AppButton>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}
